<script setup lang="ts">
    import {
        useNumericQueryParam,
        useQueryParam,
        useQueryString,
    } from '@/js/composables/useQueryString';
    import { useTablePageSize } from '@/js/composables/useTablePageSize';
    import { FilterStoreName } from '@/js/services/api/filter/types';
    import { ResponseColumnHeader } from '@/js/services/api/interaction/types';
    import { useGetOrganisationalResultsTable } from '@/js/services/api/organisationalResults/organisationalResultsQueries';
    import { __ } from '@/js/stores/expressions';
    import { useFilterStoreByName } from '@/js/stores/filter';
    import {
        InteractionTableStoreName,
        useInteractionTableStoreByName,
    } from '@/js/stores/interactions-table';
    import { storeToRefs } from 'pinia';
    import { Column, DataTable, DataTableColumnReorderEvent, PageState } from 'primevue';
    import { computed, nextTick, ref, watch } from 'vue';
    import TableLoadingOverlay from '../../dataTable/TableLoadingOverlay.vue';
    import OrganisationalLevelBreadCrumbs from './OrganisationalLevelBreadCrumbs.vue';
    import ColumnBody from '../../dataTable/ColumnBody.vue';
    import ColumnHeader from '../../dataTable/ColumnHeader.vue';
    import TablePaginator from '../../dataTable/TablePaginator.vue';
    import TableToolbar from '../../dataTable/TableToolbar.vue';
    import OrganisationalTableLegend from './OrganisationalTableLegend.vue';
    import ToggleColumnModal from '../../dataTable/ToggleColumnModal.vue';
    import FrozenColumnFade from '../../dataTable/FrozenColumnFade.vue';
    import BottomScrollFade from '../../dataTable/BottomScrollFade.vue';
    import EchoButton from '../../EchoButton.vue';
    import { PlusIcon } from '@lucide/vue';

    const filterStore = useFilterStoreByName(FilterStoreName.SharedFilter);

    const { requestData } = storeToRefs(filterStore);

    const tableStore = useInteractionTableStoreByName(
        InteractionTableStoreName.OrganisationalTable
    );

    const querystring = useQueryString();

    const page = useNumericQueryParam('page', {
        startIndex: 0,
        oneBased: true,
        updateMode: 'push',
    });
    const pageSize = useTablePageSize('organisational', 50);
    const { value: sortFieldId } = useQueryParam('field', {
        defaultValue: '',
        updateMode: 'replace',
    });
    const { value: sortOrder } = useQueryParam('order', {
        defaultValue: '',
        updateMode: 'replace',
    });

    const selectedPath = ref<string[]>([]);

    const parentLevel = computed(() => {
        if (selectedPath.value.length === 0) {
            return null;
        }
        return selectedPath.value[selectedPath.value.length - 1];
    });

    const { data: tableData, isFetching } = useGetOrganisationalResultsTable(
        page,
        pageSize,
        sortFieldId,
        sortOrder,
        requestData,
        parentLevel
    );

    const isTotalRow = (row: any) => {
        return row?.name === 'Total';
    };

    const tableKey = ref(0);

    watch(tableData, (newData) => {
        if (newData) {
            const rowIdList = newData.rowHeaders.map((row) => row.id);
            tableStore.setRowsIdList(rowIdList);
            const newIds = newData.columnHeaders
                .map((h) => h.id)
                .sort()
                .join(',');
            const currentIds = (tableStore.tableColumns ?? [])
                .map((c) => c.id)
                .sort()
                .join(',');
            if (newIds !== currentIds) {
                tableStore.setExpandableColumns(newData.columnHeaders as ResponseColumnHeader[]);
                tableKey.value++;
            }
        }
    });

    const paginationChange = async (e: PageState) => {
        const newPage = e.rows == pageSize.value ? e.page + 1 : 1;

        await querystring.setMany({ page: newPage, rows: e.rows }, 'push');
    };

    const onCustomSort = async (fieldId: string, order: 'asc' | 'desc') => {
        await querystring.setMany({ field: fieldId, order: order }, 'replace');
    };

    const tableRef = ref<HTMLElement | null>(null);
    const showColumnModal = ref(false);

    const persistTableLayout = () => {
        // TODO: Enable when BE has implemented table layout persistence and regular KPI columns have been added
    };

    const renderedColumns = computed((): ResponseColumnHeader[] => {
        if (!tableStore.tableColumns) return [];

        const columnsById = new Map(tableStore.tableColumns.map((c) => [c.id, c]));
        const result: ResponseColumnHeader[] = [];

        const addWithExpandedChildren = (col: ResponseColumnHeader) => {
            result.push(col);
            const node = tableStore.expandedColumns[col.id];
            if (node?.open) {
                [...node.children]
                    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
                    .forEach((child) => {
                        const live = columnsById.get(child.id);
                        if (live && live.order != null) addWithExpandedChildren(live);
                    });
            }
        };

        tableStore.visibleColumns
            .filter((col) => !col.expandsFromParentId)
            .forEach(addWithExpandedChildren);

        return result;
    });

    const onColumnReorder = (event: DataTableColumnReorderEvent) => {
        tableStore.onReorderTableColumn(event);
        // tableStore.ensureProtocolColumnsFirst();
        tableKey.value++;
        persistTableLayout();
    };

    const onToggleExpansion = (id: string) => {
        const scrollLeft =
            (tableRef.value?.querySelector('.p-datatable-table-container') as HTMLElement)
                ?.scrollLeft ?? 0;
        tableStore.toggleColumnExpansion(id);
        tableKey.value++;
        nextTick(() => {
            const wrapper = tableRef.value?.querySelector(
                '.p-datatable-table-container'
            ) as HTMLElement | null;
            if (wrapper) wrapper.scrollLeft = scrollLeft;
        });
    };

    const getRowValue = (column: ResponseColumnHeader) => {
        if (column.type === 'ProtocolResults') {
            return 'ProtocolResults';
        }

        return column.translationKey.split('table-protocol-column-')[1] ?? column.translationKey;
    };
</script>
<template>
    <div ref="tableRef" class="echo-table-wrapper">
        <TableToolbar
            :table-store-name="InteractionTableStoreName.OrganisationalTable"
            :show-row-actions="false"
            :add-column="false"
            :container-ref="tableRef"
        />
        <DataTable
            :key="tableKey"
            data-key="id"
            paginator
            scrollable
            :reorderable-columns="false"
            :value="tableData?.rows || []"
            :loading="isFetching"
            :total-records="tableData?.pagination.totalRows ?? 0"
            :rows="pageSize"
            :page="page"
            :first="page * pageSize"
            column-resize-mode="expand"
            :resizable-columns="false"
            @column-reorder="onColumnReorder"
        >
            <template #empty>
                <div class="h-100 flex items-center justify-center">
                    <h2 v-if="!isFetching" class="text-center">
                        {{ __('organisational-table-empty-message') }}
                    </h2>
                </div>
            </template>
            <template #loadingicon>
                <TableLoadingOverlay />
            </template>
            <Column
                field="id"
                column-key="id"
                :row-reorder="false"
                :reorderable-column="false"
                :frozen="true"
                class="min-w-70"
            >
                <template #header>
                    <div class="flex flex-col justify-between h-22 p-3 pl-6 w-full">
                        <div class="flex justify-end w-full">
                            <!-- TODO: Enable when BE has implemented table layout persistence and regular KPI columns have been added -->
                            <EchoButton
                                v-if="false"
                                size="small"
                                variant="tertiary"
                                @click="showColumnModal = true"
                            >
                                {{ __('interactions-table-add-column') }}
                                <template #icon-right><PlusIcon /></template>
                            </EchoButton>
                        </div>
                        <OrganisationalLevelBreadCrumbs
                            :selected-path="selectedPath"
                            @update:selected-path="selectedPath = $event"
                        />
                    </div>
                </template>
                <template #body="{ data }">
                    <div class="pl-3">
                        <p
                            class="text-body-bold"
                            :class="{
                                'cursor-pointer text-primary hover:underline': data.hasChildren,
                            }"
                            @click="
                                () => {
                                    if (data.hasChildren) {
                                        selectedPath.push(data.id);
                                    }
                                }
                            "
                        >
                            {{ data.name }}
                        </p>
                    </div>
                </template>
            </Column>
            <Column
                v-for="col in renderedColumns"
                :key="col.id"
                :field="col.id"
                :column-key="col.id"
                :reorderable-column="!col.expandsFromParentId"
            >
                <template #header>
                    <ColumnHeader
                        :sort-order="sortOrder as any"
                        :sort-field-id="sortFieldId || undefined"
                        :header="col"
                        :filter-store-name="FilterStoreName.SharedFilter"
                        :expandable="!!tableStore.expandedColumns[col.id]"
                        :expanded="tableStore.expandedColumns[col.id]?.open || undefined"
                        :disable-layout-shift="true"
                        :color="col.color"
                        @sort="onCustomSort"
                        @toggle-expansion="onToggleExpansion"
                        @hide-column="
                            () => {
                                tableStore.hideColumn(col.id);
                                persistTableLayout();
                            }
                        "
                    />
                </template>
                <template #body="{ data: row }">
                    <ColumnBody
                        :field="getRowValue(col)"
                        :data="row[col.id]"
                        :average-data="
                            col.type === 'ProtocolResults' && !isTotalRow(row)
                                ? tableData?.totalRow?.[col.id]
                                : undefined
                        "
                        :show-as-percentage="!tableStore.showCoachResultAsPoints"
                        :show-heat-map="
                            tableStore.showHeatMap &&
                            col.type === 'ProtocolResults' &&
                            !isTotalRow(row)
                        "
                    />
                </template>
            </Column>
            <template #paginatorcontainer="slotProps">
                <OrganisationalTableLegend />
                <TablePaginator
                    :first="slotProps.first"
                    :rows="slotProps.rows"
                    :page="slotProps.page"
                    :total-records="slotProps.totalRecords ?? 0"
                    :page-count="slotProps.pageCount ?? 0"
                    @pagination-change="paginationChange"
                />
            </template>
        </DataTable>
        <FrozenColumnFade :container-ref="tableRef" />
        <BottomScrollFade :container-ref="tableRef" />
    </div>
    <!-- TODO: Enable when BE has implemented table layout persistence and regular KPI columns have been added -->
    <ToggleColumnModal
        v-if="false"
        :store-name="InteractionTableStoreName.OrganisationalTable"
        :show-column-modal="showColumnModal"
        @close="showColumnModal = false"
        @submit="persistTableLayout"
    />
</template>
